<!-- components/SearchSelect/index.vue -->
<template>
  <div class="search-select" v-click-outside="handleClickOutside">
    <!-- 选择框主体 -->
    <div
        class="search-select__trigger"
        :class="{
        'is-focus': isOpen,
        'is-disabled': disabled,
        'is-empty': selectedOptions.length === 0
      }"
        :style="{ height: triggerHeight }"
        @click="toggleDropdown"
    >
      <!-- 选中内容区域 -->
      <div class="search-select__content" v-if="selectedOptions.length > 0">
        <div class="search-select__tags-wrapper">
          <transition-group name="tag-fade">
            <span
                v-for="(option, index) in displayTags"
                :key="option._id || getOptionKey(option)"
                class="search-select__tag"
                :style="{ maxWidth: tagMaxWidth }"
            >
              <!-- 优先显示标签，没有则显示值 -->
              <span class="tag-text" :title="getOptionLabel(option) || getOptionValue(option)">
                {{ getOptionLabel(option) || getOptionValue(option) }}
              </span>
              <i
                  class="el-icon-close tag-close"
                  @click.stop="removeTag(option, index)"
              ></i>
            </span>
          </transition-group>

          <!-- 超出数量提示 -->
          <span
              v-if="selectedOptions.length > collapseTagsMax"
              class="search-select__tag search-select__tag--more"
          >
            +{{ selectedOptions.length - collapseTagsMax }}
          </span>
        </div>
      </div>

      <!-- 空状态提示 -->
      <span
          v-if="selectedOptions.length === 0"
          class="search-select__placeholder"
      >
        {{ placeholder }}
      </span>

      <!-- 清空按钮 -->
      <i
          v-if="clearable && selectedOptions.length > 0"
          class="el-icon-circle-close search-select__clear"
          @click.stop="clearAll"
      ></i>

      <!-- 下拉箭头 -->
      <i
          class="el-icon-arrow-down search-select__arrow"
          :class="{ 'is-reverse': isOpen }"
      ></i>
    </div>

    <!-- 下拉菜单 -->
    <transition name="el-zoom-in-top">
      <div
          v-show="isOpen"
          class="search-select__dropdown"
          :style="{ width: dropdownWidth }"
      >
        <!-- 搜索框 -->
        <div class="search-select__search" v-if="filterable">
          <el-input
              v-model="searchText"
              placeholder="请输入关键字搜索"
              prefix-icon="el-icon-search"
              size="small"
              clearable
              @input="handleSearch"
              ref="searchInput"
          />
        </div>

        <!-- 全选 & 清空操作栏 -->
        <div class="search-select__actions" v-if="showCheckAll && multiple">
          <el-checkbox
              :value="isCheckAll"
              :indeterminate="isIndeterminate"
              @change="handleCheckAll"
          >
            全选
          </el-checkbox>
          <span class="action-text" @click="clearAll">清空</span>
        </div>

        <!-- 选项列表 -->
        <div class="search-select__options" ref="optionsContainer">
          <!-- 无搜索结果 -->
          <div
              v-if="filterable && filteredOptions.length === 0"
              class="search-select__empty"
          >
            <i class="el-icon-search"></i>
            <span>无匹配结果</span>
          </div>

          <!-- 正常选项 -->
          <div
              v-for="option in filteredOptions"
              :key="getOptionKey(option)"
              class="search-select__option"
              :class="{
              'is-selected': isOptionSelected(option),
              'is-disabled': option.disabled
            }"
              @click="handleOptionClick(option)"
          >
            <el-checkbox
                :value="isOptionSelected(option)"
                :disabled="option.disabled"
                @click.native.prevent="handleOptionClick(option)"
            />
            <span class="option-label" :title="getOptionLabel(option)">
              {{ getOptionLabel(option) }}
            </span>
            <i
                v-if="isOptionSelected(option)"
                class="el-icon-check check-icon"
            ></i>
          </div>
        </div>

        <!-- 底部信息 -->
        <div class="search-select__footer" v-if="showFooter && multiple">
          <span class="footer-text">
            已选择 {{ selectedOptions.length }} 项
          </span>
          <el-button type="primary" size="mini" @click="confirmSelection">
            确 定
          </el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SearchSelect',

  props: {
    // 绑定值
    value: {
      type: [String, Number, Array],
      default: () => []
    },
    // 选项数据
    options: {
      type: Array,
      default: () => []
    },
    // 选项标签字段（可配置多个，依次查找）
    labelField: {
      type: [String, Array],
      default: () => ['label', 'name', 'title', 'text']
    },
    // 选项值字段（可配置多个，依次查找）
    valueField: {
      type: [String, Array],
      default: () => ['value', 'code', 'id', 'key']
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请选择'
    },
    // 是否可搜索
    filterable: {
      type: Boolean,
      default: true
    },
    // 是否可多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 是否可清除
    clearable: {
      type: Boolean,
      default: true
    },
    // 是否显示全选
    showCheckAll: {
      type: Boolean,
      default: true
    },
    // 是否显示底部确认按钮
    showFooter: {
      type: Boolean,
      default: false
    },
    // 标签展示最大数量
    collapseTagsMax: {
      type: Number,
      default: 3
    },
    // 选择框高度
    triggerHeight: {
      type: String,
      default: 'auto'
    },
    // 标签最大宽度
    tagMaxWidth: {
      type: String,
      default: '100px'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 下拉框宽度
    dropdownWidth: {
      type: String,
      default: '100%'
    },
    // 当找不到选项时，是否使用值本身作为显示文本
    useValueAsFallback: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isOpen: false,
      searchText: '',
      selectedOptions: [],
      selectedValues: []
    }
  },

  computed: {
    // 标签字段数组
    labelFields() {
      return Array.isArray(this.labelField) ? this.labelField : [this.labelField]
    },

    // 值字段数组
    valueFields() {
      return Array.isArray(this.valueField) ? this.valueField : [this.valueField]
    },

    // 过滤后的选项
    filteredOptions() {
      if (!this.searchText) {
        return this.options
      }
      const search = this.searchText.toLowerCase().trim()
      return this.options.filter(option => {
        const label = this.getOptionLabel(option).toLowerCase()
        return label.includes(search)
      })
    },

    // 是否全选
    isCheckAll() {
      const selectableOptions = this.filteredOptions.filter(o => !o.disabled)
      return (
          selectableOptions.length > 0 &&
          selectableOptions.every(o => this.isOptionSelected(o))
      )
    },

    // 全选状态（半选）
    isIndeterminate() {
      const selected = this.filteredOptions.filter(
          o => this.isOptionSelected(o) && !o.disabled
      ).length
      const total = this.filteredOptions.filter(o => !o.disabled).length
      return selected > 0 && selected < total
    },

    // 显示的标签
    displayTags() {
      return this.selectedOptions.slice(0, this.collapseTagsMax)
    }
  },

  watch: {
    // 监听外部值变化
    value: {
      immediate: true,
      handler(val) {
        this.syncSelectedOptions()
      }
    },
    // 监听 options 变化，重新同步选项
    options: {
      deep: true,
      handler() {
        this.syncSelectedOptions()
      }
    },
    // 关闭时重置搜索
    isOpen(val) {
      if (!val) {
        this.searchText = ''
      }
    }
  },

  directives: {
    // 点击外部指令
    clickOutside: {
      bind(el, binding) {
        el.__clickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.__clickOutsideHandler__)
      },
      unbind(el) {
        document.removeEventListener('click', el.__clickOutsideHandler__)
        delete el.__clickOutsideHandler__
      }
    }
  },

  methods: {
    // ==========================================
    // 核心修复：智能获取选项标签
    // 支持多种字段名称
    // ==========================================
    getOptionLabel(option) {
      if (!option || typeof option !== 'object') {
        return option
      }

      // 优先尝试配置的字段
      for (const field of this.labelFields) {
        if (option[field] !== undefined && option[field] !== null) {
          return option[field]
        }
      }

      // 尝试常见的标签字段
      const commonLabelFields = ['label', 'name', 'title', 'text', 'displayName']
      for (const field of commonLabelFields) {
        if (option[field] !== undefined && option[field] !== null) {
          return option[field]
        }
      }

      return ''
    },

    // ==========================================
    // 核心修复：智能获取选项值
    // 支持多种字段名称
    // ==========================================
    getOptionValue(option) {
      if (!option || typeof option !== 'object') {
        return option
      }

      // 优先尝试配置的字段
      for (const field of this.valueFields) {
        if (option[field] !== undefined && option[field] !== null) {
          return option[field]
        }
      }

      // 尝试常见的值字段
      const commonValueFields = ['value', 'code', 'id', 'key']
      for (const field of commonValueFields) {
        if (option[field] !== undefined && option[field] !== null) {
          return option[field]
        }
      }

      return option
    },

    // 获取选项唯一标识
    getOptionKey(option) {
      const value = this.getOptionValue(option)
      return value
    },

    // 检查选项是否被选中
    isOptionSelected(option) {
      const value = this.getOptionValue(option)
      return this.selectedValues.some(v => v === value || String(v) === String(value))
    },

    // 切换下拉
    toggleDropdown() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      if (this.isOpen && this.filterable) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },

    // 关闭下拉
    closeDropdown() {
      this.isOpen = false
    },

    // 点击外部
    handleClickOutside() {
      this.closeDropdown()
    },

    // 搜索处理
    handleSearch() {
      this.$emit('search', this.searchText)
    },

    // 选项点击
    handleOptionClick(option) {
      if (option.disabled) return

      const value = this.getOptionValue(option)
      const index = this.selectedValues.findIndex(
          v => v === value || String(v) === String(value)
      )

      if (index > -1) {
        // 取消选中
        this.selectedValues.splice(index, 1)
        this.selectedOptions.splice(index, 1)
      } else {
        // 选中
        if (this.multiple) {
          this.selectedValues.push(value)
          this.selectedOptions.push({ ...option, _id: Date.now() + Math.random() })
        } else {
          // 单选模式
          this.selectedValues = [value]
          this.selectedOptions = [{ ...option, _id: Date.now() }]
          this.closeDropdown()
        }
      }

      this.emitChange()
    },

    // 移除标签
    removeTag(option, index) {
      const value = this.getOptionValue(option)
      const valueIndex = this.selectedValues.findIndex(
          v => v === value || String(v) === String(value)
      )

      if (valueIndex > -1) {
        this.selectedValues.splice(valueIndex, 1)
        this.selectedOptions.splice(index, 1)
      }

      this.emitChange()
    },

    // 全选/取消全选
    handleCheckAll(val) {
      if (val) {
        // 全选
        const selectableOptions = this.filteredOptions.filter(o => !o.disabled)
        selectableOptions.forEach(option => {
          const value = this.getOptionValue(option)
          if (!this.selectedValues.some(v => v === value || String(v) === String(value))) {
            this.selectedValues.push(value)
            this.selectedOptions.push({ ...option, _id: Date.now() + Math.random() })
          }
        })
      } else {
        // 取消全选
        const selectableValues = this.filteredOptions
            .filter(o => !o.disabled)
            .map(o => this.getOptionValue(o))

        this.selectedValues = this.selectedValues.filter(
            v => !selectableValues.some(sv => sv === v || String(sv) === String(v))
        )
        this.selectedOptions = this.selectedOptions.filter(
            o => !selectableValues.some(sv => sv === this.getOptionValue(o) || String(sv) === String(this.getOptionValue(o)))
        )
      }

      this.emitChange()
    },

    // 清空所有
    clearAll() {
      this.selectedValues = []
      this.selectedOptions = []
      this.emitChange()
      this.$emit('clear')
    },

    // 确认选择
    confirmSelection() {
      this.closeDropdown()
      this.$emit('confirm', {
        values: [...this.selectedValues],
        options: [...this.selectedOptions]
      })
    },

    // ==========================================
    // 核心修复：智能同步已选选项
    // 处理 options 异步加载、字段名不匹配等情况
    // ==========================================
    syncSelectedOptions() {
      // 获取当前值（确保是数组）
      let values = this.multiple
          ? (Array.isArray(this.value) ? this.value : [this.value])
          : [this.value]

      // 过滤空值
      values = values.filter(v => v !== '' && v !== null && v !== undefined)
      this.selectedValues = [...values]

      // 如果 options 为空且 useValueAsFallback 为 true，使用值本身作为显示
      if (!this.options || this.options.length === 0) {
        if (this.useValueAsFallback) {
          this.selectedOptions = values.map(v => ({
            value: v,
            label: v,
            code: v,
            id: v,
            key: v,
            _fallback: true // 标记为回退数据
          }))
        } else {
          this.selectedOptions = []
        }
        return
      }

      // 从 options 中查找对应的选项
      this.selectedOptions = values.map(val => {
        // 在 options 中查找匹配的值
        const foundOption = this.options.find(opt => {
          const optValue = this.getOptionValue(opt)
          return optValue === val || String(optValue) === String(val)
        })

        if (foundOption) {
          // 找到对应的选项，克隆并添加唯一ID
          return { ...foundOption, _id: Date.now() + Math.random() }
        } else if (this.useValueAsFallback) {
          // 没找到，使用值本身作为回退显示
          console.warn(`[SearchSelect] 未找到值 "${val}" 对应的选项，使用值本身作为显示文本`)
          return {
            value: val,
            label: val,
            code: val,
            id: val,
            key: val,
            name: val,
            _fallback: true,
            _id: Date.now() + Math.random()
          }
        } else {
          return null
        }
      }).filter(Boolean)
    },

    // 触发值变更事件
    emitChange() {
      const value = this.multiple
          ? [...this.selectedValues]
          : this.selectedValues[0] || null

      this.$emit('input', value)
      this.$emit('change', {
        value,
        values: [...this.selectedValues],
        options: [...this.selectedOptions]
      })
    },

    // 外部设置值
    setValue(value) {
      if (value === '' || value === null || value === undefined) {
        this.clearAll()
        return
      }
      const values = Array.isArray(value) ? value : [value]
      this.selectedValues = values
      this.syncSelectedOptions()
      this.emitChange()
    },

    // 外部更新选项数据
    setOptions(options) {
      this.$emit('update:options', options)
      this.syncSelectedOptions()
    }
  }
}
</script>

<style scoped>
/* ========================================
   基础样式（与之前相同）
   ======================================== */
.search-select {
  position: relative;
  width: 100%;
}

.search-select__trigger {
  display: flex;
  align-items: flex-start;
  min-height: 36px;
  padding: 4px 30px 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  overflow: hidden;
}

.search-select__trigger:hover {
  border-color: #c0c4cc;
}

.search-select__trigger.is-focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.search-select__trigger.is-disabled {
  background-color: #f5f7fa;
  cursor: not-allowed;
  opacity: 0.6;
}

.search-select__trigger.is-empty {
  align-items: center;
  padding: 0 30px 0 8px;
}

/* 选中内容区域 */
.search-select__content {
  flex: 1;
  overflow: hidden;
}

.search-select__tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

/* 标签样式 */
.search-select__tag {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 6px 0 8px;
  margin: 2px 0;
  background-color: #f0f2f5;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  box-sizing: border-box;
  flex-shrink: 0;
}

.search-select__tag .tag-text {
  display: inline-block;
  max-width: v-bind(tagMaxWidth);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
}

.search-select__tag .tag-close {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.search-select__tag .tag-close:hover {
  color: #f56c6c;
}

.search-select__tag--more {
  background-color: #409eff;
  color: #fff;
}

/* 回退数据样式（找不到选项时） */
.search-select__tag--fallback {
  background-color: #fef0f0;
  border: 1px dashed #f56c6c;
}

/* 占位符 */
.search-select__placeholder {
  color: #c0c4cc;
  font-size: 14px;
  white-space: nowrap;
  line-height: 28px;
}

/* 清空按钮 */
.search-select__clear {
  position: absolute;
  right: 28px;
  color: #c0c4cc;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;
}

.search-select__clear:hover {
  color: #909399;
}

/* 下拉箭头 */
.search-select__arrow {
  position: absolute;
  right: 8px;
  color: #c0c4cc;
  font-size: 14px;
  transition: transform 0.3s;
}

.search-select__arrow.is-reverse {
  transform: rotate(180deg);
}

/* 下拉菜单 */
.search-select__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 2200;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}

/* 搜索框 */
.search-select__search {
  padding: 10px;
  border-bottom: 1px solid #e4e7ed;
}

/* 操作栏 */
.search-select__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
}

.action-text {
  color: #409eff;
  font-size: 12px;
  cursor: pointer;
}

.action-text:hover {
  color: #66b1ff;
}

/* 选项列表 */
.search-select__options {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}

.search-select__option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.search-select__option:hover {
  background-color: #f5f7fa;
}

.search-select__option.is-selected {
  background-color: #ecf5ff;
  color: #409eff;
}

.search-select__option.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.search-select__option.is-disabled:hover {
  background-color: transparent;
}

.search-select__option .option-label {
  flex: 1;
  margin-left: 8px;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-select__option .check-icon {
  margin-left: 8px;
  color: #409eff;
}

/* 空状态 */
.search-select__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  color: #909399;
}

.search-select__empty i {
  font-size: 24px;
  margin-bottom: 8px;
}

/* 底部信息 */
.search-select__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid #e4e7ed;
}

.search-select__footer .footer-text {
  color: #909399;
  font-size: 12px;
}

/* 标签动画 */
.tag-fade-enter-active,
.tag-fade-leave-active {
  transition: all 0.2s;
}

.tag-fade-enter,
.tag-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>
